import decode from "jwt-decode";
import { Auth0Lock } from "auth0-lock";

import {
  AUTH0_CLIENT_ID,
  AUTH0_CLIENT_DOMAIN,
  AUTH0_CALLBACK,
  AUTH0_AUDIENCE,
} from "@/common/config";
import { getIdToken, getAccessToken, clearAllStorage } from "@/common/storage";
import { setUserGroups, getUserGroups } from "./storage";
import { hasGroupByName } from "@/services/user/user.service";
import ROUTER from "@/router";
import { getLockedFeatures } from "@/common/featureLock";
import store from "@/store";

import { deleteToken as unregisterDeviceForNotifications } from "@/services/common/notification.service";
import { getTopicsConfig } from "@/services/events/topics.service";
import { getRiskCategoriesConfig } from "@/services/controlRisks/risks.service";

export { _login as login };
export { _logout as logout };
export { _requireAuth as requireAuth };
export { _isLoggedIn as isLoggedIn };
export { _checkSession as checkSession };
export { _getRequestHeaders as getRequestHeaders };

let lockShowMessage = null;
let lock;
let checkSessionPromise;

const _loadAuth0Lock = (options) => {
  lock = new Auth0Lock(AUTH0_CLIENT_ID, AUTH0_CLIENT_DOMAIN, {
    container: "lockholder",
    configurationBaseUrl: "https://cdn.auth0.com",
    theme: {
      logo: require("@/assets/images/seerist_app_icon_144x144.png"),
      authButtons: {
        connectionName: {
          displayName: "Seerist",
          foregroundColor: "#333",
        },
      },
      primaryColor: "#000",
    },
    closable: false,
    languageDictionary: {
      title: "Welcome to Seerist",
      signupTitle: "CREATE ACCOUNT",
      signUpLabel: "USER SIGNUP",
      loginTitle: "LOG IN",
      loginLabel: "LOG IN",
      success: {
        signUp:
          "Thanks for signing up. A verification email has been sent. Please click on 'CONFIRM MY ACCOUNT' link within the email to log into Seerist.",
        logIn: "<br>Thanks for logging in! <br>Redirecting you to Seerist.",
      },
    },
    clientID: AUTH0_CLIENT_ID,
    domain: AUTH0_CLIENT_DOMAIN,
    auth: {
      redirectUrl:
        options && options.redirectUrl ? options.redirectUrl : AUTH0_CALLBACK,
      responseType: "token id_token",
      audience: AUTH0_AUDIENCE,
      sso: false, // if this is set to false then the "last time you logged in" popup won't appear after logging out
      params: {
        scope: "openid email",
        state: "callback",
        registration_token:
          options && options.registration_token
            ? options.registration_token
            : undefined,
      },
    },
    _enableIdPInitiatedLogin: true,
    loginAfterSignUp: false,
    initialScreen:
      options && options.initialScreen ? options.initialScreen : "login",
    allowLogin: true,
    allowSignUp: options && options.showRegistration ? true : false,
    additionalSignUpFields: [
      {
        name: "signup_first_name",
        placeholder: "First Name",
        validator: function (name) {
          let valid = name ? true : false;
          return {
            valid: valid,
          };
        },
      },
      {
        name: "signup_last_name",
        placeholder: "Last Name",
        validator: function (name) {
          let valid = name ? true : false;
          return {
            valid: valid,
          };
        },
      },
      {
        name: "signup_organization",
        placeholder: "your organization (optional)",
        validator: function () {
          return {
            valid: true,
          };
        },
      },
      {
        type: "hidden",
        name: "registration_token",
        value:
          options && options.registration_token
            ? options.registration_token
            : undefined,
      },
    ],
  });

  lock.on("authenticated", (authResult) => {
    lock.getUserInfo(authResult.accessToken, (error, profile) => {
      localStorage.setItem("accessToken", authResult.accessToken);
      localStorage.setItem("profile", JSON.stringify(profile));
    });
    lock.hide();
  });

  lock.on("authorization_error", (err) => {
    lockShowMessage = "There was an issue signing in.<br>Please try again.";
    console.error("Auth0 Lock authorization_error", err);
  });

  lock.on("unrecoverable_error", (err) => {
    lockShowMessage = "There was an issue signing in.<br>Please try again.";
    console.error("Auth0 Lock unrecoverable_error", err);
  });
};

const _login = (options) => {
  _loadAuth0Lock(options);

  let lockShowOptions = {};
  if (lockShowMessage) {
    lockShowOptions = {
      flashMessage: {
        type: "error",
        text: `<div class="mb-3">${lockShowMessage}</div><div>If the issue persists please contact Seerist Support at help@seerist.io or 800-674-8398.</div>`,
      },
    };
  }

  lock.show(lockShowOptions);
  lockShowMessage = null;
};

const _logout = async () => {
  await unregisterDeviceForNotifications();
  clearAllStorage();
  ROUTER.go("/");
};

const _setLockedFeatures = async () => {
  if (store) {
    const LOCKED_FEATURES = store.getters["home/lockedFeatures"];
    if (!LOCKED_FEATURES) {
      await getLockedFeatures();
    }
  }
};

const _requireAuth = async (to, from, next) => {
  if (!(await _isLoggedIn())) {
    next({
      path: "/",
      query: { redirect: to.fullPath },
    });
  } else {
    await getTopicsConfig();
    await getRiskCategoriesConfig();
    await setUserGroups(); /* TODO: Decide if we want this call here or only on login.
                                    With it here if we change user groups we don't need
                                    a logout/login (just a refresh), but the call is made more frequently. */

    const GROUPS = getUserGroups();
    if (GROUPS) {
      if (Array.isArray(GROUPS) && GROUPS.length) {
        if (!hasGroupByName("Accepted_Terms_And_Privacy_Policy")) {
          return next("/eula");
        }
        if (hasGroupByName("Mobile_Only")) {
          return next("/mobile-only");
        }
        await _setLockedFeatures();
        next();
      } else {
        console.log("User currently assigned to no groups, so new user.");
        return next("/eula");
      }
    } else {
      console.log(
        "Unable to sign in due to session or groups.",
        "GROUPS=",
        GROUPS
      );
      lockShowMessage = "There was an issue signing in.<br>Please try again.";
      next({
        path: "/",
        query: { redirect: to.fullPath },
      });
    }
  }
};

const hasTokens = function () {
  const ID_TOKEN = getIdToken();
  const ACCESS_TOKEN = getAccessToken();
  return ID_TOKEN && ACCESS_TOKEN;
};

const attemptSilentAuthentication = async function () {
  if (!hasTokens()) {
    return false;
  }
  const threshold = 10;
  const ID_TOKEN = getIdToken();
  const ACCESS_TOKEN = getAccessToken();
  const now = new Date();
  const minsTilExpireID = (_getTokenExpirationDate(ID_TOKEN) - now) / 1000 / 60;
  const minsTilExpireACCESS =
    (_getTokenExpirationDate(ACCESS_TOKEN) - now) / 1000 / 60;
  if (minsTilExpireID < threshold || minsTilExpireACCESS < threshold) {
    await _checkSession();
  }
};

const _isLoggedIn = async () => {
  const ID_TOKEN = getIdToken();

  await attemptSilentAuthentication();

  if (!!ID_TOKEN && !_isTokenExpired(ID_TOKEN)) {
    return true;
  } else {
    if (ROUTER && ROUTER.currentRoute.path !== "/") {
      _logout();
    }
    clearAllStorage();
    return false;
  }
};

//Attempts to reauthenticate user
const _checkSession = async () => {
  _loadAuth0Lock();

  if (checkSessionPromise) {
    return await checkSessionPromise;
  }

  checkSessionPromise = new Promise((resolve, reject) => {
    lock.checkSession({}, function (err, authResult) {
      if (err) {
        _logout();
        resolve(false);
      } else {
        if (!authResult.accessToken) {
          _logout();
          resolve(false);
        }
        lock.getUserInfo(authResult.accessToken, function (error, profile) {
          checkSessionPromise = undefined;
          localStorage.setItem("access_token", authResult.accessToken);
          localStorage.setItem("user_profile", JSON.stringify(profile));
          localStorage.setItem("id_token", authResult.idToken);
          resolve(true);
        });
      }
    });
  });
  return checkSessionPromise;
};

const _getTokenExpirationDate = (encodedToken) => {
  const TOKEN = decode(encodedToken);
  if (!TOKEN.exp) {
    return null;
  }

  const DATE = new Date(0);
  DATE.setUTCSeconds(TOKEN.exp);

  return DATE;
};

const _isTokenExpired = (token) => {
  const EXPIRATION_DATE = _getTokenExpirationDate(token);
  return EXPIRATION_DATE < new Date();
};

const _getRequestHeaders = () => {
  return {
    Authorization: "Bearer " + getAccessToken(),
    "Content-type": "application/json",
  };
};
